import React from 'react';
import { Button, Theme, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  buildInvalidOptionKey,
  buildNotOnMenuKey,
  DISMISS_REPEAT_TTS,
} from '../../redux/features/unresolvedItems/unresolvedItems.constants';
import {
  selectUnresolvedItemsCount,
  selectVisibleInvalidOptionLines,
  selectVisibleNotOnMenuCards,
} from '../../redux/features/unresolvedItems/unresolvedItems.selector';
import { dismissAllUnresolvedItems } from '../../redux/features/unresolvedItems/unresolvedItems.slice';
import { selectValidCartItems } from '../../selectors/cart';
import useTTSMessage from '../../hooks/useTTSMessage.hooks';
import useReadback from '../../hooks/useReadback/useReadback.hooks';
import Colors from '../../utils/color';
import UnresolvedItemCard from './UnresolvedItemCard';

const useStyles = makeStyles(({ spacing }: Theme) => ({
  container: {
    marginBottom: spacing(2),
    paddingBottom: spacing(1),
    borderBottom: `${spacing(0.125)} solid ${Colors.alto}`,
  },
  header: {
    fontWeight: 'bold !important',
    color: `${Colors.internationalOrange} !important`,
  },
  buttonsWrapper: {
    display: 'flex',
    gap: spacing(1),
    marginTop: spacing(2),
  },
  dismissRepeatButton: {
    flex: 1,
    textTransform: 'none !important' as any,
    fontWeight: 'bold !important',
  },
  dismissButton: {
    flex: 1,
    textTransform: 'none !important' as any,
    fontWeight: 'bold !important',
  },
}));

/**
 * Per-session panel surfacing the items the AI flagged as unresolved
 * (NOT_ON_MENU + invalid options) as diagnostic cards. When the agent clicks
 * "Dismiss & Repeat", a natural TTS message is sent followed by a readback
 * request so the customer hears the current order, and the panel is dismissed.
 */
export default function UnresolvedItemsPanel() {
  const {
    container,
    header,
    buttonsWrapper,
    dismissRepeatButton,
    dismissButton,
  } = useStyles();
  const dispatch = useAppDispatch();
  const { sendTTSMessage } = useTTSMessage();
  const { handleReadBack } = useReadback();

  const notOnMenuCards = useAppSelector(selectVisibleNotOnMenuCards);
  const invalidOptionLines = useAppSelector(selectVisibleInvalidOptionLines);
  const count = useAppSelector(selectUnresolvedItemsCount);
  const validCartItems = useAppSelector(selectValidCartItems);
  const hasCartItems = Object.keys(validCartItems).length > 0;

  if (count === 0) {
    return null;
  }

  const handleDismissAndRepeat = () => {
    sendTTSMessage(DISMISS_REPEAT_TTS);
    handleReadBack({ timePressed: Date.now(), forceRegularReadback: true });
    dispatch(dismissAllUnresolvedItems());
  };

  const handleDismiss = () => {
    dispatch(dismissAllUnresolvedItems());
  };

  return (
    <div id="unresolvedItems" className={container}>
      <Typography variant="subtitle2" className={header}>
        Unresolved items ({count})
      </Typography>
      {notOnMenuCards.map((card) => (
        <UnresolvedItemCard
          key={`not-on-menu-${buildNotOnMenuKey(card)}`}
          variant="notOnMenu"
          title={card.item}
          diagnosticLabel="couldn't resolve — not on menu"
        />
      ))}
      {invalidOptionLines.map((line) => (
        <UnresolvedItemCard
          key={`invalid-option-${buildInvalidOptionKey(line)}`}
          variant="invalidOption"
          title={`"${line.option}" in ${
            line.is_top_level ? 'top-level' : line.constraint ?? 'top-level'
          }`}
          diagnosticLabel="invalid option"
        />
      ))}
      <div className={buttonsWrapper}>
        <Button
          variant="outlined"
          color="warning"
          size="small"
          className={dismissRepeatButton}
          onClick={handleDismissAndRepeat}
          disabled={!hasCartItems}
        >
          Dismiss &amp; Repeat
        </Button>
        <Button
          variant="contained"
          color="warning"
          size="small"
          className={dismissButton}
          onClick={handleDismiss}
        >
          Dismiss
        </Button>
      </div>
    </div>
  );
}
